/**
 * TeenCharterCelebration.js — A one-time, dismissible celebration banner
 * that fires the moment a teen's Family Charter goes `active` (both
 * parties signed). Solves the "no acknowledgement when teen completes
 * Charter" gap.
 *
 * Dismissal is persisted in localStorage keyed on the charter_id so it
 * never re-shows for the same charter. New charters (e.g. parent + teen
 * + additional sibling) will trigger a fresh celebration.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Download, X, Trophy, PartyPopper } from 'lucide-react';
import { apiClient } from '../../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_PREFIX = 'hibff:charter-celebrated:';

const TeenCharterCelebration = () => {
  const navigate = useNavigate();
  const [charter, setCharter] = useState(null);
  const [charterPdf, setCharterPdf] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await apiClient.request('/api/family/charter').catch(() => null);
        if (cancelled || !c || c.status !== 'active') return;
        const already = localStorage.getItem(`${LS_PREFIX}${c.charter_id}`);
        if (already) { setDismissed(true); return; }
        setCharter(c);
        // Fire-and-forget PDF lookup so we can offer a one-click download
        const pdfResp = await apiClient.request('/api/family/charter/my-pdf').catch(() => null);
        if (!cancelled && pdfResp?.charter_pdfs?.length > 0) {
          setCharterPdf(pdfResp.charter_pdfs[0]);
        }
      } catch {
        /* silent — celebration is non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    if (charter?.charter_id) {
      localStorage.setItem(`${LS_PREFIX}${charter.charter_id}`, '1');
    }
    setDismissed(true);
  };

  if (!charter || dismissed) return null;

  return (
    <div
      data-testid="teen-charter-celebration"
      className="mb-6 relative overflow-hidden rounded-2xl p-5 sm:p-6"
      style={{
        background: 'linear-gradient(135deg, #FFF5EA 0%, #FFE6D5 35%, #FCE7F3 100%)',
        border: '1.5px solid #F5C99F',
        boxShadow: '0 2px 8px rgba(245,131,31,0.10)',
      }}
    >
      {/* Decorative confetti dots */}
      <div className="absolute -top-3 -right-3 w-24 h-24 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #F5831F 0%, transparent 65%)' }} />
      <div className="absolute -bottom-6 -left-4 w-20 h-20 rounded-full opacity-25" style={{ background: 'radial-gradient(circle, #7B4FD8 0%, transparent 65%)' }} />

      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/50 transition-colors"
        aria-label="Dismiss celebration"
        data-testid="teen-charter-celebration-dismiss"
      >
        <X size={16} className="text-gray-600" />
      </button>

      <div className="relative flex items-start gap-4">
        <div
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: 'linear-gradient(135deg, #F5831F, #FFB073)' }}
        >
          <Trophy size={26} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <PartyPopper size={14} style={{ color: '#F5831F' }} />
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#A65812' }}>
              Charter active
            </span>
          </div>
          <h3 className="text-lg sm:text-xl font-bold mb-1.5" style={{ color: '#1A1209' }}>
            You&apos;re officially HiBFF, {(charter.teen_name || 'friend').split(' ')[0]}!
          </h3>
          <p className="text-sm leading-relaxed mb-3" style={{ color: '#5A4030' }}>
            You and <strong>{charter.parent_name || 'your parent'}</strong> have both signed your
            Family Charter. Your full HiBFF experience is now unlocked — practise with your AvAs,
            earn badges, and build your skills together.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            {charterPdf && (
              <a
                href={`${API_URL}${charterPdf.pdf_url}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="teen-charter-celebration-pdf"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-colors"
                style={{ backgroundColor: '#1A1209', color: '#FFFFFF' }}
              >
                <Download size={14} /> Download my Charter
              </a>
            )}
            <button
              onClick={() => navigate('/charter')}
              data-testid="teen-charter-celebration-view"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-colors"
              style={{ backgroundColor: '#F5831F', color: '#FFFFFF' }}
            >
              <Sparkles size={14} /> See what&apos;s unlocked
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeenCharterCelebration;
